<template>
  <div
    v-if="configuration.hasOwnProperty('autosampler') && configuration.autosampler != null"
    class="hardware-tile autosampler"
  >
    <div class="hardware-tile__wrapper-title">
      <div>
        <i class="hardware-tile__title-icon">group_work</i>
        <h4>Autosampler</h4>
      </div>
      <Information :data="popupInfoData" class="hardware-tile__information" />
    </div>

    <HardwareStateComponent
      :state="currentState"
      :errors="errors"
      data-test-id="autosampler-status"
    />
    <HardwareErrorComponent :errors="errors" :errorList="errorList" />

    <!--  OPERATIONS  -->

    <template v-if="operation === 'INJECTION'">
      <div class="operation text-process">
        <i>colorize</i>
        <span>
          Injecting {{ stateDetails.amount }} µL
          <br />
          from {{ getDisplayVial(stateDetails.vial) }}
        </span>
      </div>

      <template v-if="stateDetails.vial && stateDetails.vial > 0">
        <div class="inline-block font-black mt-2 text-2xl">{{ displayVial }}</div>
        <div class="font-black text-gray">Vial</div>
      </template>
    </template>

    <div v-else-if="operation === 'NEEDLE_WASHING'" class="operation text-process">
      <i>gradient</i>
      <span>
        Washing needle
        <template v-if="stateDetails.remaining_needle_washes > 0">
          <br />
          <span class="operation__remaining">
            Remaining: {{ stateDetails.remaining_needle_washes }}
            {{ stateDetails.remaining_needle_washes === 1 ? 'cycle' : 'cycles' }}
          </span>
        </template>
      </span>
    </div>

    <div v-else-if="operation === 'INJECTOR_WASHING'" class="operation text-process">
      <i>water_drop</i>
      <span>
        Washing injector
        <template v-if="stateDetails.remaining_injector_washes > 0">
          <br />
          <span class="operation__remaining">
            Remaining: {{ stateDetails.remaining_injector_washes }}
            {{ stateDetails.remaining_injector_washes === 1 ? 'cycle' : 'cycles' }}
          </span>
        </template>
      </span>
    </div>

    <div v-else-if="operation === 'SHAKING'" class="operation text-process">
      <i>sync_alt</i>
      <span>Shaking</span>
    </div>

    <div v-else-if="operation === 'GETTING_READY'" class="operation text-yellow">
      <i>rotate_left</i>
      <span>Getting ready</span>
    </div>

    <div v-else-if="operation === 'WASHING_LOOP'" class="operation text-yellow">
      <i>360</i>
      <span>Washing loop</span>
    </div>

    <template v-else>
      <div v-if="!injectionInProgress" class="hardware-tile__wrapper-vial">
        <BtnSelectVial
          :isDisabled="!device.isMethodCompatible || state.ready !== true"
          :trayConfig="trayConfig"
          @select="setVial"
        />
      </div>

      <div v-if="canWashNeedle || canWashInjector || canShake" class="autosampler__actions">
        <BtnSelectWashVial v-if="canWashNeedle" class="autosampler__action" @select="washNeedle">
          Wash needle
        </BtnSelectWashVial>
        <Btn
          v-if="canWashInjector"
          class="autosampler__action"
          icon-left="water_drop"
          height="s"
          @click="washInjector"
        >
          Wash injector
        </Btn>
        <Btn
          v-if="canShake"
          class="autosampler__action"
          icon-left="sync_alt"
          height="s"
          @click="shake"
        >
          Shake
        </Btn>
      </div>
    </template>

    <div v-if="canAbortShake || canAbortWashInjector || canAbortWashNeedle" class="mt-8">
      <Btn
        v-if="canAbortWashNeedle"
        class="autosampler__action"
        icon-left="stop"
        height="s"
        @click="abortWashNeedle"
        >Cancel needle washing</Btn
      >
      <Btn
        v-if="canAbortShake"
        class="autosampler__action"
        icon-left="stop"
        height="s"
        @click="abortShake"
      >
        Cancel shaking
      </Btn>
      <Btn
        v-if="canAbortWashInjector"
        class="autosampler__action"
        icon-left="stop"
        height="s"
        @click="abortWashInjector"
      >
        Cancel injector washing
      </Btn>
    </div>

    <!--Absolute positioned-->
    <BtnIcon
      :isDisabled="state.autosampler_details == null"
      iconMaterial="settings"
      class="autosampler__btn-service"
      @click="showServiceModal"
    />
    <AutosamplerServiceModal
      v-if="state.autosampler_details"
      :autosamplerState="state.autosampler_details"
      :deviceSocket="ws"
    />
  </div>
</template>

<script>
  import Base from './Base.vue';
  import 'assets/css/base/animation.scss';
  import BtnSelectVial from '../../../uikitProject/vials/vueBtnSelectVial/BtnSelectVial';
  import BtnSelectWashVial from '@/uikitProject/vials/vueBtnSelectWashVial/BtnSelectWashVial';
  import Btn from '@/uikitBase/btns/Btn';
  import HardwareErrorComponent from 'components/element/HardwareErrorComponent';
  import { AUTOSAMPLER_ERRORS, GENERAL_ERRORS } from '@/constants/hardware/errors';
  import Information from './private/Information';
  import { autosamplerCommands } from '@/constants/hardware/commands';
  import BtnIcon from '@/uikitBase/btns/BtnIcon.vue';
  import AutosamplerServiceModal from '@/components/block/modal/AutosamplerServiceModal.vue';
  import { startInjection } from '@/utils/injectionHelpers';
  import { getVialLabel } from '@/utils/trayHelpers';

  export default {
    components: {
      AutosamplerServiceModal,
      BtnIcon,
      Information,
      HardwareErrorComponent,
      Btn,
      BtnSelectWashVial,
      BtnSelectVial,
    },

    extends: Base,

    props: {
      device: {
        type: Object,
        required: true,
      },
    },

    data: () => ({
      vial: null,
    }),

    computed: {
      currentState() {
        return this.state?.autosampler;
      },
      stateDetails() {
        return this.state?.autosampler_details;
      },
      operation() {
        return this.stateDetails?.operation;
      },
      errors() {
        return this.stateDetails?.errors;
      },
      errorList() {
        return { ...GENERAL_ERRORS, ...AUTOSAMPLER_ERRORS };
      },
      injectionInProgress() {
        return this.state.injection_id != null;
      },
      displayVial() {
        return this.getDisplayVial(this.vial || this.stateDetails?.vial);
      },

      popupInfoData() {
        const { firmware_version, id, serial } = this.configuration.autosampler;

        const data = [
          {
            name: 'Id',
            value: id,
          },
          {
            name: 'Serial',
            value: serial,
          },
          {
            name: 'Firmware Version',
            value: firmware_version
              ? `${firmware_version.major}.${firmware_version.minor}`
              : 'Not available',
          },
          {
            name: 'Tray',
            value: this.trayConfig,
          },
          {
            name: 'Total injections',
            value: this.state?.autosampler_details?.total_injections,
          },
          {
            name: 'Valve position',
            value: this.state?.autosampler_details?.valve_position,
          },
        ];

        return data;
      },

      commands() {
        return this.stateDetails?.commandDetails ?? {};
      },

      canWashNeedle() {
        return this.commands[autosamplerCommands.WASH_NEEDLE]?.status.available;
      },
      canWashInjector() {
        return this.commands[autosamplerCommands.WASH_INJECTOR]?.status.available;
      },
      canShake() {
        return this.commands[autosamplerCommands.SHAKE]?.status.available;
      },
      canAbortShake() {
        return (
          this.operation === 'SHAKING' &&
          this.commands[autosamplerCommands.ABORT_SHAKE]?.status.available
        );
      },
      canAbortWashInjector() {
        return (
          this.operation === 'INJECTOR_WASHING' &&
          this.commands[autosamplerCommands.ABORT_WASH_INJECTOR]?.status.available
        );
      },
      canAbortWashNeedle() {
        return (
          this.operation === 'NEEDLE_WASHING' &&
          this.commands[autosamplerCommands.ABORT_WASH_NEEDLE]?.status.available
        );
      },
    },

    methods: {
      getDisplayVial(vial) {
        return getVialLabel(vial, this.trayConfig);
      },
      setVial(vial) {
        this.vial = vial;
        this.start();
      },

      async start() {
        try {
          await startInjection(this.device, this.vial);
          this.notify('Starting');
        } catch (e) {
          this.notifyError('Can not start injection');
          console.warn('start error', e);
        }
      },

      washNeedle(vial) {
        this.ws.command('autosampler', autosamplerCommands.WASH_NEEDLE, { vial });
      },
      washInjector() {
        this.ws.command('autosampler', autosamplerCommands.WASH_INJECTOR);
      },
      shake() {
        this.ws.command('autosampler', autosamplerCommands.SHAKE);
      },
      abortShake() {
        this.ws.command('autosampler', autosamplerCommands.ABORT_SHAKE);
      },
      abortWashInjector() {
        this.ws.command('autosampler', autosamplerCommands.ABORT_WASH_INJECTOR);
      },
      abortWashNeedle() {
        this.ws.command('autosampler', autosamplerCommands.ABORT_WASH_NEEDLE);
      },

      showServiceModal() {
        this.$modal.show('autosamplerService');
      },
    },
  };
</script>

<style lang="scss">
  @import '~assets/css/base/icons';
  @import '~assets/css/base/text';
  @import '~assets/css/base/animation';

  .hardware-tile {
    .operation {
      text-align: center;
      align-content: center;
      margin: 32px auto auto;

      span {
        font-weight: bold;
        margin-bottom: 4px;
      }

      &__remaining {
        font-weight: normal !important;
        color: $color-text-second;
      }

      i {
        @extend .material-icons;
        @extend .material-icon--24;
        @extend .blink;
        display: block;
        margin: auto;
      }
    }
  }

  .autosampler {
    position: relative;

    &:hover &__btn-service {
      opacity: 1;
    }

    &__btn-service {
      @include isHoverDevice {
        opacity: 0;
      }

      position: absolute;
      bottom: 16px;
      right: 16px;
      transition: opacity 0.3s;
    }

    &__actions {
      margin-top: 32px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__action {
      width: fit-content;
    }
  }
</style>
